import * as React from "react";
import { forwardRef, useCallback, useMemo, useRef, useState } from "react";
import type {
  EditorState,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  SerializedEditorState,
  SerializedLexicalNode
} from "lexical";
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getSelection,
  $insertNodes,
  $isRangeSelection,
  CAN_REDO_COMMAND,
  CAN_UNDO_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_NORMAL,
  FORMAT_ELEMENT_COMMAND,
  FORMAT_TEXT_COMMAND,
  REDO_COMMAND,
  SELECTION_CHANGE_COMMAND,
  UNDO_COMMAND,
  createCommand
} from "lexical";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { $generateHtmlFromNodes } from "@lexical/html";
import {
  HeadingNode,
  QuoteNode,
  $createHeadingNode,
  $createQuoteNode,
  $isHeadingNode
} from "@lexical/rich-text";
import {
  ListNode,
  ListItemNode,
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
  $isListNode
} from "@lexical/list";
import { LinkNode, TOGGLE_LINK_COMMAND, $createLinkNode, $isLinkNode } from "@lexical/link";
import { CodeNode, $createCodeNode, $isCodeNode } from "@lexical/code";
import { TRANSFORMERS } from "@lexical/markdown";
import { mergeRegister } from "@lexical/utils";
import { $setBlocksType } from "@lexical/selection";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Code as InlineCodeIcon,
  CodeSquare,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  Image as ImageIcon,
  Link as LinkIcon,
  Link2Off,
  List as BulletListIcon,
  ListOrdered,
  Pilcrow,
  Quote as QuoteIcon,
  Redo,
  Strikethrough,
  Underline,
  Undo
} from "lucide-react";
import { useForm } from "react-hook-form";

import { cn } from "@/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/input";
import { ImageNode, $createImageNode, $isImageNode, type ImagePayload } from "./ImageNode";

type RichTextSerializedState = string;

type ToolbarBlockType = "paragraph" | "h1" | "h2" | "h3" | "bullet" | "number" | "quote" | "code";

type EditorSerializedState = SerializedEditorState<SerializedLexicalNode>;

type ImageUploadResult = ImagePayload | string;

type RichTextProps = {
  value?: RichTextSerializedState;
  defaultValue?: RichTextSerializedState;
  onChange?: (serializedState: RichTextSerializedState, editorState: EditorState) => void;
  onHtmlChange?: (html: string, editorState: EditorState) => void;
  placeholder?: string;
  readOnly?: boolean;
  disabled?: boolean;
  editorClassName?: string;
  toolbarClassName?: string;
  autoFocus?: boolean;
  onImageUpload?: (file: File) => Promise<ImageUploadResult>;
  onImageUploadError?: (error: unknown) => void;
  acceptImageMimeTypes?: string;
  allowImageUrlInsert?: boolean;
} & React.HTMLAttributes<HTMLDivElement>;

const theme = {
  paragraph: "mb-2 leading-6 text-sm text-foreground",
  placeholder:
    "pointer-events-none absolute left-0 top-0 px-3 py-2 text-sm text-muted-foreground select-none",
  quote: "border-l-2 border-border pl-3 italic text-muted-foreground",
  heading: {
    h1: "scroll-m-20 text-2xl font-semibold tracking-tight",
    h2: "scroll-m-20 text-xl font-semibold tracking-tight",
    h3: "scroll-m-20 text-lg font-semibold tracking-tight"
  },
  list: {
    listitem: "ml-4 marker:text-muted-foreground [&:not(:last-child)]:mb-1",
    nested: {
      listitem: "ml-4"
    },
    ol: "ml-4 list-decimal pl-1",
    ul: "ml-4 list-disc pl-1"
  },
  text: {
    bold: "font-semibold",
    italic: "italic",
    underline: "underline underline-offset-4",
    strikethrough: "line-through",
    code: "rounded bg-muted px-1 py-[2px] font-mono text-[13px]"
  },
  link: "text-primary underline underline-offset-4",
  code: "relative block overflow-x-auto rounded-md border border-border bg-muted px-3 py-2 font-mono text-sm text-foreground shadow-sm"
};

const nodes = [HeadingNode, QuoteNode, ListNode, ListItemNode, LinkNode, CodeNode, ImageNode];

const INSERT_IMAGE_COMMAND = createCommand<ImagePayload>("INSERT_IMAGE_COMMAND");
const DEFAULT_IMAGE_ACCEPT = "image/*";

function parseSerializedEditorState(raw?: RichTextSerializedState): EditorSerializedState | undefined {
  if (!raw) {
    return undefined;
  }

  try {
    return JSON.parse(raw) as EditorSerializedState;
  } catch (error) {
    console.error("[RichText] Failed to parse editor state:", error);
    return undefined;
  }
}

function Placeholder({ placeholder }: { placeholder: string }) {
  return <div className={theme.placeholder}>{placeholder}</div>;
}

type ToolbarToggleState = {
  isBold: boolean;
  isItalic: boolean;
  isUnderline: boolean;
  isStrikethrough: boolean;
  isCode: boolean;
  isLink: boolean;
  blockType: ToolbarBlockType;
  elementFormat: ElementFormatType | null;
  canUndo: boolean;
  canRedo: boolean;
};

const initialToolbarState: ToolbarToggleState = {
  isBold: false,
  isItalic: false,
  isUnderline: false,
  isStrikethrough: false,
  isCode: false,
  isLink: false,
  blockType: "paragraph",
  elementFormat: null,
  canUndo: false,
  canRedo: false
};

type ToolbarButtonProps = {
  label: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
};

function ToolbarButton({ label, icon: Icon, onClick, active, disabled }: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={label}
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "inline-flex h-8 w-8 items-center justify-center rounded-md text-sm transition-colors",
        "text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1",
        active ? "bg-accent text-accent-foreground shadow-sm" : "",
        disabled ? "pointer-events-none opacity-40" : ""
      )}
    >
      <Icon className="h-4 w-4" aria-hidden />
      <span className="sr-only">{label}</span>
    </button>
  );
}

type ToolbarPluginProps = {
  disabled: boolean;
  onImageUpload?: (file: File) => Promise<ImageUploadResult>;
  onImageUploadError?: (error: unknown) => void;
  allowImageUrlInsert?: boolean;
  acceptImageMimeTypes?: string;
};

type LinkFormValues = {
  url: string;
  label: string;
};

type ImageFormValues = {
  url: string;
  alt: string;
  width: string;
  height: string;
};

function placeCaretAfterImage(imageNode: ImageNode) {
  const nextSibling = imageNode.getNextSibling();

  if (!nextSibling || $isImageNode(nextSibling)) {
    const paragraph = $createParagraphNode();
    const textNode = $createTextNode("");
    paragraph.append(textNode);
    imageNode.insertAfter(paragraph);
    textNode.select(0, 0);
    return;
  }

  imageNode.selectNext(0, 0);
}

function ToolbarPlugin({
  disabled,
  onImageUpload,
  onImageUploadError,
  allowImageUrlInsert = true,
  acceptImageMimeTypes
}: ToolbarPluginProps) {
  const [editor] = useLexicalComposerContext();
  const [state, setState] = useState<ToolbarToggleState>(initialToolbarState);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isLinkDialogOpen, setLinkDialogOpen] = useState(false);
  const [editingExistingLink, setEditingExistingLink] = useState(false);
  const [linkNodeKey, setLinkNodeKey] = useState<NodeKey | null>(null);
  const [isImageDialogOpen, setImageDialogOpen] = useState(false);
  const [editingExistingImage, setEditingExistingImage] = useState(false);
  const [imageNodeKey, setImageNodeKey] = useState<NodeKey | null>(null);

  const linkForm = useForm<LinkFormValues>({
    defaultValues: { url: "", label: "" }
  });

  const imageForm = useForm<ImageFormValues>({
    defaultValues: { url: "", alt: "", width: "", height: "" }
  });

  const openImageDialog = useCallback(() => {
    if (disabled || !allowImageUrlInsert) {
      return;
    }

    const targetImage = editor.getEditorState().read((): ReturnType<typeof $createImageNode> | null => {
      const selection = $getSelection();
      if (!$isRangeSelection(selection)) {
        return null;
      }

      const nodes = selection.getNodes();
      for (const node of nodes) {
        if ($isImageNode(node)) {
          return node;
        }
      }

      return null;
    });

    const width = targetImage?.getWidth();
    const height = targetImage?.getHeight();
    const initialUrl = targetImage?.getSrc() ?? "";
    const initialAlt = targetImage?.getAltText() ?? "";
    const initialWidth = typeof width === "number" ? String(width) : "";
    const initialHeight = typeof height === "number" ? String(height) : "";

    imageForm.clearErrors();
    imageForm.reset({
      url: initialUrl,
      alt: initialAlt,
      width: initialWidth,
      height: initialHeight
    });
    setEditingExistingImage(Boolean(targetImage));
    setImageNodeKey(targetImage?.getKey() ?? null);
    setImageDialogOpen(true);
  }, [allowImageUrlInsert, disabled, editor, imageForm]);

  const closeImageDialog = useCallback(() => {
    setImageDialogOpen(false);
    setEditingExistingImage(false);
    setImageNodeKey(null);
    imageForm.reset();
    setTimeout(() => {
      editor.focus();
    }, 0);
  }, [editor, imageForm]);

  const openLinkDialog = useCallback(() => {
    if (disabled) {
      return;
    }

    const { initialUrl, initialLabel, detectedLink } = editor.getEditorState().read(
      (): {
        initialUrl: string;
        initialLabel: string;
        detectedLink: ReturnType<typeof $createLinkNode> | null;
      } => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return { initialUrl: "", initialLabel: "", detectedLink: null };
        }

        const nodes = selection.getNodes();
        let label = selection.getTextContent().trim();
        let url = "";
        let found: ReturnType<typeof $createLinkNode> | null = null;

        for (const node of nodes) {
          const linkNode =
            $isLinkNode(node) ? node
            : $isLinkNode(node.getParent()) ? (node.getParent() as LinkNode)
            : null;

          if (linkNode) {
            url = linkNode.getURL();
            if (!label) {
              label = linkNode.getTextContent().trim();
            }
            found = linkNode;
            break;
          }
        }

        if (!label && url) {
          label = url;
        }

        return { initialUrl: url, initialLabel: label, detectedLink: found };
      }
    );

    linkForm.clearErrors();
    linkForm.reset({
      url: initialUrl,
      label: initialLabel
    });
    setEditingExistingLink(Boolean(detectedLink));
    setLinkNodeKey(detectedLink?.getKey() ?? null);
    setLinkDialogOpen(true);
  }, [disabled, editor, linkForm]);

  const closeLinkDialog = useCallback(() => {
    setLinkDialogOpen(false);
    setEditingExistingLink(false);
    setLinkNodeKey(null);
    linkForm.reset();
    setTimeout(() => {
      editor.focus();
    }, 0);
  }, [editor, linkForm]);

  const handleLinkSubmit = linkForm.handleSubmit(({ url, label }) => {
    const trimmedUrl = url.trim();
    const trimmedLabel = label.trim();

    if (!trimmedUrl) {
      linkForm.setError("url", { type: "required", message: "Please enter a URL." });
      return;
    }

    editor.focus();

    editor.update(() => {
      const labelToApply = trimmedLabel || trimmedUrl;

      if (editingExistingLink && linkNodeKey) {
        const existingNode = $getNodeByKey<LexicalNode>(linkNodeKey);
        if ($isLinkNode(existingNode)) {
          existingNode.setURL(trimmedUrl);
          existingNode.clear();
          existingNode.append($createTextNode(labelToApply));
          existingNode.select();
          return;
        }
      }

      const selection = $getSelection();
      const linkNode = $createLinkNode(trimmedUrl);
      linkNode.append($createTextNode(labelToApply));

      if ($isRangeSelection(selection)) {
        selection.insertNodes([linkNode]);
      } else {
        $insertNodes([linkNode]);
      }

      linkNode.select();
    });

    closeLinkDialog();
  });

  const handleRemoveLink = useCallback(() => {
    editor.focus();
    editor.update(() => {
      if (linkNodeKey) {
        const node = $getNodeByKey<LexicalNode>(linkNodeKey);
        if ($isLinkNode(node)) {
          node.remove();
          return;
        }
      }

      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, null);
      }
    });

    closeLinkDialog();
  }, [closeLinkDialog, editor, linkNodeKey]);

  const handleLinkDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setLinkDialogOpen(true);
      } else {
        closeLinkDialog();
      }
    },
    [closeLinkDialog]
  );

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }

    const anchorNode = selection.anchor.getNode();
    const element = anchorNode.getKey() === "root" ? anchorNode : anchorNode.getTopLevelElementOrThrow();
    let blockType: ToolbarBlockType = "paragraph";

    let parentNode: LexicalNode | null = anchorNode;

    while (parentNode) {
      if ($isListNode(parentNode)) {
        blockType = parentNode.getTag() === "ol" ? "number" : "bullet";
        break;
      }
      parentNode = parentNode.getParent();
    }

    if (blockType === "paragraph") {
      if ($isHeadingNode(element)) {
        blockType = element.getTag() as ToolbarBlockType;
      } else if ($isCodeNode(element)) {
        blockType = "code";
      } else if (element.getType() === "quote") {
        blockType = "quote";
      }
    }

    const nodes = selection.getNodes();
    const hasLink = nodes.some((node) => $isLinkNode(node) || $isLinkNode(node.getParent()));
    const elementFormat =
      (element as unknown as { getFormatType?: () => ElementFormatType | null }).getFormatType?.() ??
      null;

    setState((current) => ({
      ...current,
      blockType,
      elementFormat,
      isBold: selection.hasFormat("bold"),
      isItalic: selection.hasFormat("italic"),
      isUnderline: selection.hasFormat("underline"),
      isStrikethrough: selection.hasFormat("strikethrough"),
      isCode: selection.hasFormat("code"),
      isLink: hasLink
    }));
  }, []);

  React.useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        CAN_UNDO_COMMAND,
        (payload) => {
          setState((current) => ({ ...current, canUndo: payload }));
          return false;
        },
        COMMAND_PRIORITY_NORMAL
      ),
      editor.registerCommand(
        CAN_REDO_COMMAND,
        (payload) => {
          setState((current) => ({ ...current, canRedo: payload }));
          return false;
        },
        COMMAND_PRIORITY_NORMAL
      ),
      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(updateToolbar);
      })
    );
  }, [editor, updateToolbar]);

  const applyBlock = useCallback(
    (nextBlock: ToolbarBlockType) => {
      if (disabled) {
        return;
      }

      editor.update(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return;
        }

        switch (nextBlock) {
          case "paragraph": {
            $setBlocksType(selection, () => $createParagraphNode());
            break;
          }
          case "h1":
          case "h2":
          case "h3": {
            if (state.blockType === nextBlock) {
              $setBlocksType(selection, () => $createParagraphNode());
            } else {
              $setBlocksType(selection, () => $createHeadingNode(nextBlock));
            }
            break;
          }
          case "bullet": {
            if (state.blockType === "bullet") {
              editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
            } else {
              editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            }
            break;
          }
          case "number": {
            if (state.blockType === "number") {
              editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined);
            } else {
              editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            }
            break;
          }
          case "quote": {
            if (state.blockType === "quote") {
              $setBlocksType(selection, () => $createParagraphNode());
            } else {
              $setBlocksType(selection, () => $createQuoteNode());
            }
            break;
          }
          case "code": {
            if (state.blockType === "code") {
              $setBlocksType(selection, () => $createParagraphNode());
            } else {
              $setBlocksType(selection, () => $createCodeNode());
            }
            break;
          }
          default:
            break;
        }
      });
    },
    [disabled, editor, state.blockType]
  );

  const toggleFormat = useCallback(
    (format: "bold" | "italic" | "underline" | "strikethrough" | "code") => {
      if (disabled) {
        return;
      }

      editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
    },
    [disabled, editor]
  );

  const toggleLink = useCallback(() => {
    openLinkDialog();
  }, [openLinkDialog]);

  const applyElementFormat = useCallback(
    (format: ElementFormatType) => {
      if (disabled) {
        return;
      }

      editor.dispatchCommand(FORMAT_ELEMENT_COMMAND, format);
    },
    [disabled, editor]
  );

  const canInsertImage = allowImageUrlInsert || Boolean(onImageUpload);
  const imageAccept = acceptImageMimeTypes ?? DEFAULT_IMAGE_ACCEPT;

  const handleInsertImage = useCallback(() => {
    if (disabled || !canInsertImage) {
      return;
    }

    editor.focus();

    if (onImageUpload) {
      fileInputRef.current?.click();
      return;
    }

    openImageDialog();
  }, [canInsertImage, disabled, editor, onImageUpload, openImageDialog]);

  const handleFileChange = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!onImageUpload) {
        return;
      }

      const file = event.target.files?.[0];

      try {
        if (!file) {
          return;
        }

        const result = await onImageUpload(file);
        const payload =
          typeof result === "string" ?
            { src: result, altText: file.name }
          : {
              src: result.src,
              altText: result.altText ?? file.name,
              width: result.width,
              height: result.height
            };

        editor.dispatchCommand(INSERT_IMAGE_COMMAND, payload);
        editor.focus();
      } catch (error) {
        onImageUploadError?.(error);
        console.error("[RichText] Failed to upload image:", error);
      } finally {
        event.target.value = "";
      }
    },
    [editor, onImageUpload, onImageUploadError]
  );

  const handleImageSubmit = imageForm.handleSubmit(({ url, alt, width, height }) => {
    const trimmedUrl = url.trim();
    const trimmedAlt = alt.trim();
    const trimmedWidth = width.trim();
    const trimmedHeight = height.trim();

    if (!trimmedUrl) {
      imageForm.setError("url", { type: "required", message: "Please enter an image URL." });
      return;
    }

    let parsedWidth: number | undefined;
    if (trimmedWidth) {
      const widthValue = Number(trimmedWidth);
      if (Number.isNaN(widthValue) || widthValue <= 0) {
        imageForm.setError("width", {
          type: "validate",
          message: "Width must be a positive number."
        });
        return;
      }
      parsedWidth = widthValue;
    }

    let parsedHeight: number | undefined;
    if (trimmedHeight) {
      const heightValue = Number(trimmedHeight);
      if (Number.isNaN(heightValue) || heightValue <= 0) {
        imageForm.setError("height", {
          type: "validate",
          message: "Height must be a positive number."
        });
        return;
      }
      parsedHeight = heightValue;
    }

    editor.focus();

    editor.update(() => {
      const payload: ImagePayload = {
        src: trimmedUrl,
        altText: trimmedAlt || undefined,
        width: parsedWidth ?? undefined,
        height: parsedHeight ?? undefined
      };

      if (editingExistingImage && imageNodeKey) {
        const existingNode = $getNodeByKey<LexicalNode>(imageNodeKey);
        if ($isImageNode(existingNode)) {
          existingNode.setSrc(payload.src);
          existingNode.setAltText(payload.altText ?? "");
          existingNode.setDimensions(payload.width ?? "inherit", payload.height ?? "inherit");
          placeCaretAfterImage(existingNode);
          return;
        }
      }

      const selection = $getSelection();
      const imageNode = $createImageNode(payload);

      if ($isRangeSelection(selection)) {
        selection.insertNodes([imageNode]);
      } else {
        $insertNodes([imageNode]);
      }

      placeCaretAfterImage(imageNode);
    });

    closeImageDialog();
  });

  const handleImageDialogOpenChange = useCallback(
    (open: boolean) => {
      if (open) {
        setImageDialogOpen(true);
      } else {
        closeImageDialog();
      }
    },
    [closeImageDialog]
  );

  const handleRemoveImage = useCallback(() => {
    if (!imageNodeKey) {
      closeImageDialog();
      return;
    }

    editor.focus();
    editor.update(() => {
      const existingNode = $getNodeByKey<LexicalNode>(imageNodeKey);
      if ($isImageNode(existingNode)) {
        existingNode.remove();
      }
    });

    closeImageDialog();
  }, [closeImageDialog, editor, imageNodeKey]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-1">
        <ToolbarButton
          label="Undo"
          icon={Undo}
          onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)}
          disabled={disabled || !state.canUndo}
        />
        <ToolbarButton
          label="Redo"
          icon={Redo}
          onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)}
          disabled={disabled || !state.canRedo}
        />
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        <ToolbarButton
          label="Bold"
          icon={Bold}
          onClick={() => toggleFormat("bold")}
          active={state.isBold}
          disabled={disabled}
        />
        <ToolbarButton
          label="Italic"
          icon={Italic}
          onClick={() => toggleFormat("italic")}
          active={state.isItalic}
          disabled={disabled}
        />
        <ToolbarButton
          label="Underline"
          icon={Underline}
          onClick={() => toggleFormat("underline")}
          active={state.isUnderline}
          disabled={disabled}
        />
        <ToolbarButton
          label="Strikethrough"
          icon={Strikethrough}
          onClick={() => toggleFormat("strikethrough")}
          active={state.isStrikethrough}
          disabled={disabled}
        />
        <ToolbarButton
          label="Inline code"
          icon={InlineCodeIcon}
          onClick={() => toggleFormat("code")}
          active={state.isCode}
          disabled={disabled}
        />
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        <ToolbarButton
          label="Paragraph"
          icon={Pilcrow}
          onClick={() => applyBlock("paragraph")}
          active={state.blockType === "paragraph"}
          disabled={disabled}
        />
        <ToolbarButton
          label="Heading 1"
          icon={Heading1}
          onClick={() => applyBlock("h1")}
          active={state.blockType === "h1"}
          disabled={disabled}
        />
        <ToolbarButton
          label="Heading 2"
          icon={Heading2}
          onClick={() => applyBlock("h2")}
          active={state.blockType === "h2"}
          disabled={disabled}
        />
        <ToolbarButton
          label="Heading 3"
          icon={Heading3}
          onClick={() => applyBlock("h3")}
          active={state.blockType === "h3"}
          disabled={disabled}
        />
        <ToolbarButton
          label="Bulleted list"
          icon={BulletListIcon}
          onClick={() => applyBlock("bullet")}
          active={state.blockType === "bullet"}
          disabled={disabled}
        />
        <ToolbarButton
          label="Numbered list"
          icon={ListOrdered}
          onClick={() => applyBlock("number")}
          active={state.blockType === "number"}
          disabled={disabled}
        />
        <ToolbarButton
          label="Quote"
          icon={QuoteIcon}
          onClick={() => applyBlock("quote")}
          active={state.blockType === "quote"}
          disabled={disabled}
        />
        <ToolbarButton
          label="Code block"
          icon={CodeSquare}
          onClick={() => applyBlock("code")}
          active={state.blockType === "code"}
          disabled={disabled}
        />
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        <ToolbarButton
          label={state.isLink ? "Edit link" : "Insert link"}
          icon={LinkIcon}
          onClick={toggleLink}
          active={state.isLink}
          disabled={disabled}
        />
        {state.isLink ?
          <ToolbarButton
            label="Remove link"
            icon={Link2Off}
            onClick={handleRemoveLink}
            disabled={disabled}
          />
        : null}
        <ToolbarButton
          label="Insert image"
          icon={ImageIcon}
          onClick={handleInsertImage}
          disabled={disabled || !canInsertImage}
        />
        {onImageUpload ?
          <input
            ref={fileInputRef}
            type="file"
            accept={imageAccept}
            className="hidden"
            onChange={handleFileChange}
          />
        : null}
        <span className="mx-1 h-5 w-px bg-border" aria-hidden />
        <ToolbarButton
          label="Align left"
          icon={AlignLeft}
          onClick={() => applyElementFormat("left")}
          active={state.elementFormat === "left" || state.elementFormat === null}
          disabled={disabled}
        />
        <ToolbarButton
          label="Align center"
          icon={AlignCenter}
          onClick={() => applyElementFormat("center")}
          active={state.elementFormat === "center"}
          disabled={disabled}
        />
        <ToolbarButton
          label="Align right"
          icon={AlignRight}
          onClick={() => applyElementFormat("right")}
          active={state.elementFormat === "right"}
          disabled={disabled}
        />
      </div>
      <Dialog open={isLinkDialogOpen} onOpenChange={handleLinkDialogOpenChange}>
        <DialogContent
          className="min-w-[70vw] max-w-7xl"
          header={editingExistingLink ? "Edit link" : "Insert link"}
        >
          <Form {...linkForm}>
            <form className="grid gap-4 p-6" onSubmit={handleLinkSubmit}>
              <FormField
                control={linkForm.control}
                name="url"
                rules={{ required: "Please enter a URL." }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://example.com" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={linkForm.control}
                name="label"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Link label</FormLabel>
                    <FormControl>
                      <Input placeholder="Display text" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <DialogFooter className="pt-2">
                <Button type="button" variant="cancel" onClick={closeLinkDialog}>
                  Cancel
                </Button>
                {editingExistingLink ?
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleRemoveLink}
                    className="text-destructive"
                  >
                    Remove link
                  </Button>
                : null}
                <Button type="submit">Save link</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <Dialog open={isImageDialogOpen} onOpenChange={handleImageDialogOpenChange}>
        <DialogContent header={editingExistingImage ? "Edit image" : "Insert image"}>
          <Form {...imageForm}>
            <form className="grid gap-4 p-6" onSubmit={handleImageSubmit}>
              <FormField
                control={imageForm.control}
                name="url"
                rules={{ required: "Please enter an image URL." }}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Image URL</FormLabel>
                    <FormControl>
                      <Input placeholder="https://example.com/image.jpg" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={imageForm.control}
                name="alt"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Alt text</FormLabel>
                    <FormControl>
                      <Input placeholder="Describe the image" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <FormField
                  control={imageForm.control}
                  name="width"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Width (px)</FormLabel>
                      <FormControl>
                        <Input placeholder="Auto" inputMode="numeric" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={imageForm.control}
                  name="height"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Height (px)</FormLabel>
                      <FormControl>
                        <Input placeholder="Auto" inputMode="numeric" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter className="pt-2">
                <Button type="button" variant="cancel" onClick={closeImageDialog}>
                  Cancel
                </Button>
                {editingExistingImage ?
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleRemoveImage}
                    className="text-destructive"
                  >
                    Remove image
                  </Button>
                : null}
                <Button type="submit">{editingExistingImage ? "Save changes" : "Insert image"}</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ControlledValuePlugin({
  value,
  trackAppliedValue
}: {
  value?: RichTextSerializedState;
  trackAppliedValue: React.MutableRefObject<RichTextSerializedState | undefined>;
}) {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    if (value == null || trackAppliedValue.current === value) {
      return;
    }

    let parsed: EditorSerializedState | undefined;

    try {
      parsed = JSON.parse(value) as EditorSerializedState;
    } catch (error) {
      console.error("[RichText] Failed to sync controlled value:", error);
      return;
    }

    editor.update(() => {
      const newState = editor.parseEditorState(parsed!);
      const current = editor.getEditorState();

      // Compare serialized versions to avoid redundant updates
      if (JSON.stringify(current.toJSON()) !== JSON.stringify(newState.toJSON())) {
        editor.setEditorState(newState);
      }

      // Track the latest applied value so we don't reapply unnecessarily
      trackAppliedValue.current = value;
    });
  }, [editor, trackAppliedValue, value]);

  return null;
}

function EditableStatePlugin({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    editor.setEditable(editable);
  }, [editor, editable]);

  return null;
}

function ImagesPlugin() {
  const [editor] = useLexicalComposerContext();

  React.useEffect(() => {
    return editor.registerCommand(
      INSERT_IMAGE_COMMAND,
      (payload) => {
        if (!payload?.src) {
          return false;
        }

        editor.update(() => {
          const imageNode = $createImageNode(payload);
          $insertNodes([imageNode]);
          placeCaretAfterImage(imageNode);
        });

        return true;
      },
      COMMAND_PRIORITY_EDITOR
    );
  }, [editor]);

  return null;
}

const RichText = forwardRef<HTMLDivElement, RichTextProps>(function RichText(
  {
    value,
    defaultValue,
    onChange,
    onHtmlChange,
    placeholder,
    readOnly,
    disabled,
    editorClassName,
    toolbarClassName,
    autoFocus,
    onImageUpload,
    onImageUploadError,
    acceptImageMimeTypes = DEFAULT_IMAGE_ACCEPT,
    allowImageUrlInsert = true,
    className,
    id,
    ...rest
  },
  ref
) {
  const initialSerializedStateRef = useRef<EditorSerializedState | undefined>(undefined);

  if (initialSerializedStateRef.current === undefined) {
    initialSerializedStateRef.current = parseSerializedEditorState(value ?? defaultValue);
  }

  const initialConfig = useMemo(
    () => ({
      namespace: "RichTextEditor",
      editable: !(readOnly || disabled),
      theme,
      onError(error: Error) {
        throw error;
      },
      nodes,
      editorState:
        initialSerializedStateRef.current ? JSON.stringify(initialSerializedStateRef.current) : undefined
    }),
    [disabled, readOnly]
  );

  const appliedValueRef = useRef<RichTextSerializedState | undefined>(value ?? defaultValue);

  const handleChange = useCallback(
    (editorState: EditorState, editor: LexicalEditor) => {
      const serialized = JSON.stringify(editorState.toJSON());
      appliedValueRef.current = serialized;

      if (onChange) {
        onChange(serialized, editorState);
      }

      if (onHtmlChange) {
        let html = "";
        editorState.read(() => {
          html = $generateHtmlFromNodes(editor, null);
        });
        onHtmlChange(html, editorState);
      }
    },
    [onChange, onHtmlChange]
  );

  const editable = !(readOnly || disabled);

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div
        ref={ref}
        aria-disabled={disabled}
        data-disabled={disabled ? "true" : undefined}
        className={cn(
          "border-input focus-within:border-ring focus-within:ring-ring/50 bg-background text-sm shadow-sm focus-within:ring-[3px]",
          "flex min-h-[12rem] flex-col rounded-md border transition-[color,box-shadow]",
          "data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-60",
          className
        )}
        {...rest}
      >
        {editable ?
          <div className={cn("border-b border-border bg-muted/40 px-2 py-1.5", toolbarClassName)}>
            <ToolbarPlugin
              disabled={Boolean(disabled)}
              onImageUpload={onImageUpload}
              onImageUploadError={onImageUploadError}
              allowImageUrlInsert={allowImageUrlInsert}
              acceptImageMimeTypes={acceptImageMimeTypes}
            />
          </div>
        : null}
        <div className="relative flex-1 overflow-auto">
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                id={id}
                className={cn(
                  "h-full min-h-[10rem] whitespace-pre-wrap break-words px-3 py-2 text-sm leading-6 text-foreground outline-none",
                  editorClassName
                )}
              />
            }
            placeholder={<Placeholder placeholder={placeholder || ""} />}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <HistoryPlugin />
          <ListPlugin />
          <LinkPlugin />
          <ImagesPlugin />
          <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
          {autoFocus ?
            <AutoFocusPlugin />
          : null}
          <EditableStatePlugin editable={editable} />
          <ControlledValuePlugin value={value} trackAppliedValue={appliedValueRef} />
          <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
        </div>
      </div>
    </LexicalComposer>
  );
});

RichText.displayName = "RichText";

export { RichText };
